export type TaskStatus =
  | 'un_assigned'
  | 'todo'
  | 'waiting_for_info'
  | 'in_progress'
  | 'completed';

/** @deprecated Use TaskStatus — kept for gradual migration */
export type KanbanColumnId = TaskStatus;

export type TaskPriority = 'high' | 'normal' | 'low';

export interface TaskTypeRef {
  id: number;
  name: string;
}

export interface TaskLabelRef {
  labelId: number;
  name: string;
  colorHex: string;
}

export interface TaskUserRef {
  userId: number | string;
  name: string;
  email?: string;
  avatarUrl?: string | null;
  role?: string;
}

export interface TaskCard {
  taskId: number | string;
  taskNumber: string;
  title: string;
  email: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority | null;
  dueDate: string | null;
  type: TaskTypeRef | null;
  labels: TaskLabelRef[];
  assignee: TaskUserRef | null;
  reporter: TaskUserRef | null;
  leadId: number | string | null;
  leadUniqueId?: string | null;
  userId?: number | string | null;
  completedAt: string | null;
  isClosed: boolean;
  createdAt: string;
  modifiedAt: string;
  createdBy?: TaskUserRef | null;
}

export interface KanbanBoardColumn {
  status: TaskStatus;
  count: number;
  tasks: TaskCard[];
}

export interface BoardFilters {
  search?: string;
  status?: TaskStatus[];
  assigneeUserIds?: number[];
  priorities?: TaskPriority[];
  labelIds?: number[];
}

export interface CreateTaskRequest {
  title: string;
  email: string;
  description?: string;
  leadId?: number | null;
  taskTypeId?: number;
  status?: TaskStatus;
  priority?: TaskPriority | null;
  dueDate?: string;
  assigneeUserId?: number;
  reporterUserId?: number;
  labelIds?: number[];
}

export interface UpdateTaskRequest {
  title?: string;
  email?: string;
  description?: string;
  leadId?: number | null;
  taskTypeId?: number;
  status?: TaskStatus;
  priority?: TaskPriority | null;
  dueDate?: string;
  assigneeUserId?: number;
  reporterUserId?: number;
  labelIds?: number[];
  clearAssignee?: boolean;
  clearReporter?: boolean;
  clearPriority?: boolean;
  clearDueDate?: boolean;
  clearTaskType?: boolean;
  clearLead?: boolean;
}

export interface TaskCommentAttachment {
  fileId: number | string;
  fileName: string;
  url: string;
  mimeType: string;
}

export interface TaskComment {
  commentId: number | string;
  body: string;
  author: TaskUserRef;
  attachments: TaskCommentAttachment[];
  createdAt: string;
  modifiedAt: string;
}

export interface TaskHistoryItem {
  historyId: number | string;
  entity: 'task' | 'comment';
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  action: 'create' | 'update' | 'delete';
  changedBy: TaskUserRef;
  createdAt: string;
}

export interface TaskHistoryResponse {
  total: number;
  page: number;
  limit: number;
  items: TaskHistoryItem[];
}

export interface CreateTaskLabelRequest {
  name: string;
  colorHex: string;
}

export interface UpdateTaskLabelRequest {
  name?: string;
  colorHex?: string;
}

/** UI view model used by board cards and modal */
export interface KanbanTask {
  taskId: string;
  taskNumber: string;
  columnId: TaskStatus;
  title: string;
  email: string;
  description?: string;
  priority: TaskPriority | null;
  labels: TaskLabelRef[];
  typeName: string | null;
  dueDate: string | null;
  assignee?: { id: string; name: string; avatar?: string };
  reporter?: { id: string; name: string };
  leadId?: string;
  leadUniqueId?: string | null;
  leadUserId?: string;
  isClosed?: boolean;
  order: number;
}

export interface KanbanAssignee {
  id: string;
  name: string;
  avatar?: string;
}

export interface KanbanComment {
  id: string;
  author: string;
  authorId: string;
  text: string;
  createdAt: string;
  attachments: TaskCommentAttachment[];
}

export interface CreateKanbanTaskRequest {
  title: string;
  email: string;
  description?: string;
  columnId: TaskStatus;
  priority?: TaskPriority | null;
  taskTypeId?: number;
  dueDate?: string;
  assigneeId?: string;
  reporterId?: string;
  labelIds?: number[];
  leadId?: number;
}

export interface UpdateKanbanTaskRequest {
  title?: string;
  email?: string;
  description?: string;
  leadId?: number;
  columnId?: TaskStatus;
  priority?: TaskPriority | null;
  taskTypeId?: number | null;
  dueDate?: string | null;
  assigneeId?: string | null;
  reporterId?: string | null;
  labelIds?: number[];
  clearAssignee?: boolean;
  clearReporter?: boolean;
  clearPriority?: boolean;
  clearDueDate?: boolean;
  clearTaskType?: boolean;
  clearLead?: boolean;
}

export interface KanbanQueryParams {
  search?: string;
  status?: string;
  assignee?: string;
  priority?: string;
  label?: string;
}

export const TASK_LABEL_PALETTE = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1', '#3949AB',
  '#1E88E5', '#039BE5', '#00ACC1', '#00897B', '#43A047',
  '#7CB342', '#C0CA33', '#FDD835', '#FFB300', '#FB8C00',
  '#F4511E', '#6D4C41', '#757575', '#546E7A', '#263238',
] as const;
